<template>
  <div class="build-caption-form">
    <div class="build-caption-form__columns-section">
      <div class="build-caption-form__left-col">
        <!-- Text chunks spoilers -->
        <div v-if="shareStopSummaryStore.socialShareSummaryPayload.adventureTextSummaries.length" class="text-chunks-spoilers build-caption-form__text-chunks-spoilers">
          <div class="global-h4 text-chunks-spoilers__title">Craft a caption for your post:</div>

          <!-- Big spoiler -->
          <SrpDetailsSummary
            class="big-spoiler text-chunks-spoilers__big-spoiler"
            v-for="(adventureSummary, index) in shareStopSummaryStore.socialShareSummaryPayload.adventureTextSummaries"
            :key="adventureSummary.adventureId"
            :is-opened="index === 0"
          >
            <template #heading>
              <div class="global-h5 big-spoiler__summary">
                {{ adventureSummary.adventureTitle }}
                <router-link
                  @click.stop
                  target="_blank"
                  :to="{ name: 'ItineraryPreview', params: { itineraryId: adventureSummary.adventureId }, query: { partner: shareStopSummaryStore.editedPost?.customerId } }"
                  class="snippet__icon"
                  style="margin-left: 7px; margin-top: 2px"
                >
                  <IconEmbedded name="external-link_2" :size="17" />
                </router-link>
              </div>
            </template>
            <template #details>
              <div class="big-spoiler__content">
                <!-- Small spoiler -->
                <SrpDetailsSummary v-if="adventureSummary.adventureSummary" class="small-spoiler big-spoiler__small-spoiler" :is-opened="true">
                  <template #heading>
                    <div class="global-h6 small-spoiler__summary">
                      <b>{{ "Summary " }}</b>
                    </div>
                  </template>
                  <template #details>
                    <div class="small-spoiler__content">
                      {{ adventureSummary.adventureSummary }}

                      <CopyButton class="small-spoiler__copy-button" @onCopy="copyTextToTextarea(String(adventureSummary.adventureSummary).trim())" />
                    </div>
                  </template>
                </SrpDetailsSummary>
                <!-- / Small spoiler -->

                <!-- Small spoiler -->
                <SrpDetailsSummary
                  class="small-spoiler big-spoiler__small-spoiler"
                  v-for="(step, idx) in adventureSummary.steps"
                  :key="idx"
                  :is-opened="index === 0 && idx === 0 && !adventureSummary.adventureSummary"
                >
                  <template #heading>
                    <div class="global-h6 small-spoiler__summary">
                      <b>{{ "Step " + (idx + 1) }}</b
                      >: {{ step.locationName }}
                    </div>
                  </template>
                  <template #details>
                    <div class="small-spoiler__content">
                      {{ step.stepDetails }}

                      <CopyButton class="small-spoiler__copy-button" @onCopy="copyTextToTextarea(String(step.stepDetails).trim())" />
                    </div>
                  </template>
                </SrpDetailsSummary>
                <!-- / Small spoiler -->
              </div>
            </template>
          </SrpDetailsSummary>
          <!-- / Big spoiler -->
        </div>
        <!-- / Text chunks spoilers -->

        <SrpButton class="build-caption-form__next-button" @click="saveAndGoToNextSlide" color="orange" :is-disabled="!shareStopSummaryStore.editedPost.caption">
          Next
          <template #iconRight>
            <IconEmbedded v-if="isSaving" name="loader_3-5" color="rgba(255, 255, 255, 0.5)" :size="21" />
            <IconEmbedded v-else name="arrow-bottom_2-5" color="rgba(255, 255, 255, 0.5)" :size="21" />
          </template>
        </SrpButton>
      </div>

      <div class="build-caption-form__right-col">
        <div class="global-h4" style="margin-bottom: 13px">Post preview</div>
        <SocialPostPreview
          class="build-caption-form__post-preview-section"
          :assetIds="shareStopSummaryStore.editedPost.assetIds"
          :caption="shareStopSummaryStore.editedPost.caption || ''"
          :fancyboxKey="shareStopSummaryStore.editedPost.id"
          ref="domRefSocialPostPreview"
          @update:caption="newValue => (shareStopSummaryStore.editedPost.caption = newValue)"
        />
      </div>
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<script lang="ts">export default { name: "BuildYourCaption" };</script>

<script setup lang="ts">
import { ref, nextTick, onMounted, inject, Ref } from "vue";

// Types
import { ScreenSize } from "@contracts/screenSize";

// Stores
import { useShareStopSummaryStore } from "@stores/shareStopSummary";

// Components
import CopyButton from "@views/Community/ShareStopSummaryV2/slides/BuildYourCaption/CopyButton.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SocialPostPreview from "@components/SocialPostPreview/index.vue";

const screenSize = inject("screenSize") as Ref<ScreenSize>;

const shareStopSummaryStore = useShareStopSummaryStore();

const emit = defineEmits<{
  (e: "goToNextSlide"): void;
}>();

// Scroll textareas to bottom on text copy event ==============================
const domRefSocialPostPreview = ref(null);

async function copyTextToTextarea(text: string): Promise<void> {
  if (shareStopSummaryStore.editedPost.caption === null) shareStopSummaryStore.editedPost.caption = "";
  shareStopSummaryStore.editedPost.caption += text;

  await nextTick();
  scrollTextareaToBottom();
}

function scrollTextareaToBottom() {
  let targetTextarea;
  const { domRefTextareaFb, domRefTextareaInst } = domRefSocialPostPreview.value;

  if (domRefTextareaFb) targetTextarea = domRefTextareaFb;
  else if (domRefTextareaInst) targetTextarea = domRefTextareaInst;

  targetTextarea.scrollTop = targetTextarea.scrollHeight;
}

// Save and go to the next slide ==============================================
const isSaving = ref(false);
async function saveAndGoToNextSlide(): Promise<void> {
  isSaving.value = true;
  await shareStopSummaryStore.updatePost(shareStopSummaryStore.editedPost);

  isSaving.value = false;

  emit("goToNextSlide");
}

// Focus the textarea on load =================================================
onMounted(async () => {
  if (["mobile", "tablet"].includes(screenSize.value)) return;

  await nextTick();
  const { domRefTextareaFb, domRefTextareaInst } = domRefSocialPostPreview.value;
  if (domRefTextareaFb) domRefTextareaFb.focus();
  else if (domRefTextareaInst) domRefTextareaInst.focus();
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/mixins/animations/gradient-spin-white.scss";

// "Or" divider ===============================================================
.or-divider {
  height: 46px;
  padding-right: 80px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  position: relative;
  z-index: 0;

  &::before {
    content: "";
    width: 100%;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
    height: 0;
    position: absolute;
    inset: 50% auto auto 0;
    z-index: -1;
    font-size: 0;
    line-height: 0;
  }

  &::after {
    content: "";
    width: 125px;
    height: 100%;
    position: absolute;
    inset: 0 0 auto auto;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
  }

  &__circle {
    width: 46px;
    min-width: 46px;
    height: 46px;
    padding-bottom: 3px;
    box-sizing: border-box;
    border-radius: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    font:
      700 19px/19px "Quicksand",
      sans-serif;
    background: #ececec;
  }
}

// Small spoiler ==============================================================
.small-spoiler {
  color: #5b5b5b;
  font: 14px/20px sans-serif;

  &__summary {
    outline: none;
    position: relative;
    color: #000;
    font-weight: 600;
    opacity: 1;
    cursor: pointer;
    transition: opacity 0.07s ease-in-out;

    b {
      font-weight: 700;
    }
  }

  &__content {
    padding: 11px 24px 13px 17px;
    margin: 7px 24px 0 14px;
    border: 1px #b4b4b4 dashed;
    border-radius: 6px;
    position: relative;
    white-space: pre-wrap;
  }

  &__copy-button {
    position: absolute;
    inset: calc(50% - 19px) -19px auto auto;
    z-index: 0;
  }
}

// Big spoiler ================================================================
.big-spoiler {
  padding: 15px 14px 17px;
  border-radius: 6px;
  color: #5b5b5b;
  font: 14px/20px sans-serif;
  background: rgba(0, 0, 0, 0.05);

  &__summary {
    padding-right: 5px;
    display: flex;
    position: relative;
    color: #000;
    font:
      700 16px/21px "Quicksand",
      sans-serif;
    opacity: 1;
    cursor: pointer;
    transition: opacity 0.07s ease-in-out;
  }

  &__content {
    padding: 12px 0 0 3px;
    margin-top: 13px;
    border-top: 1px rgba(0, 0, 0, 0.1) solid;
    white-space: pre-wrap;
  }

  &__small-spoiler {
    margin-bottom: 14px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Text chunks spoilers =======================================================
.text-chunks-spoilers {
  &__title {
    margin-bottom: 14px;
    font-weight: 500;
  }

  &__big-spoiler {
    margin-bottom: 14px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Build cation form ==========================================================
.build-caption-form {
  &__columns-section {
    display: flex;
    justify-content: space-between;
    position: relative;

    &::before {
      content: "";
      width: 1px;
      height: 100%;
      position: absolute;
      inset: 0 auto auto 50%;
      background: rgba(0, 0, 0, 0.1);
      pointer-events: none;
    }

    &::after {
      content: "";
      width: 22px;
      height: 22px;
      box-sizing: border-box;
      border: 1px rgba(0, 0, 0, 0.15) solid;
      border-width: 1px 1px 0 0;
      position: absolute;
      inset: 200px auto auto calc(50% - 10px);
      transform: rotate(45deg);
      background: white;
      pointer-events: none;
    }
  }

  &__left-col {
    width: calc(50% - 54px);
  }

  &__or-divider {
    margin-bottom: 20px;
  }

  &__text-chunks-spoilers {
    margin-bottom: 30px;
  }

  &__right-col {
    width: calc(50% - 54px);
  }

  &__post-preview-section {
    margin-bottom: 35px;
  }

  &__next-button {
    outline: none;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .build-caption-form {
    &__left-col {
      width: calc(50% - 40px);
    }

    &__right-col {
      width: calc(50% - 40px);
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .build-caption-form {
    &__left-col {
      width: calc(50% - 49px);
    }

    &__right-col {
      width: calc(50% - 49px);
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .build-caption-form {
    &__columns-section {
      flex-direction: column;

      &::before {
        order: 2;
        width: 100%;
        margin-bottom: 7px;
        height: 1px;
        position: static;
        font-size: 0;
        line-height: 0;
      }

      &::after {
        order: 2;
        width: 22px;
        height: 22px;
        margin: 0 auto;
        border: 1px rgba(0, 0, 0, 0.15) solid;
        border-width: 1px 1px 0 0;
        position: relative;
        inset: -18px auto auto 0;
        transform: rotate(135deg);
      }
    }

    &__left-col {
      order: 1;
      width: 100%;
      margin-bottom: 35px;
    }

    &__right-col {
      order: 3;
      width: 100%;
    }

    &__post-preview-section {
      margin-bottom: 35px;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .build-caption-form {
    &__columns-section {
      flex-direction: column;

      &::before {
        order: 2;
        width: 100%;
        margin-bottom: 7px;
        height: 1px;
        position: static;
        font-size: 0;
        line-height: 0;
      }

      &::after {
        order: 2;
        width: 22px;
        height: 22px;
        margin: 0 auto;
        border: 1px rgba(0, 0, 0, 0.15) solid;
        border-width: 1px 1px 0 0;
        position: relative;
        inset: -18px auto auto 0;
        transform: rotate(135deg);
      }
    }

    &__left-col {
      order: 1;
      width: 100%;
      margin-bottom: 35px;
    }

    &__right-col {
      order: 3;
      width: 100%;
    }

    &__post-preview-section {
      margin-bottom: 35px;
    }
  }
}
</style>
